import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";

import styles from "./LoginPage.module.css";
import usePageTitle from "../../hooks/usePageTitle";
import { sendForgotPasswordOTP, verifyForgotPasswordOTP, resetPassword } from "../../api/auth";
import OTPInput from "../../components/OTPInput/OTPInput";

const carouselSlides = [
    {
        image: "/SignupBanner.png",
        heading: "Grow Wealth Together as a Family",
        text: "Empower your loved ones. Securely map individual Demat accounts to a shared dashboard and manage your family's investments with unified insights and high-grade security."
    },
    {
        image: "/SignupBanner.png",
        heading: "Smart Trading Made Simple",
        text: "Access real-time market data, advanced analytics, and automated trading strategies. Make informed decisions with our comprehensive platform."
    },
    {
        image: "/SignupBanner.png",
        heading: "Secure & Reliable Platform",
        text: "Your investments are protected with bank-level security. Experience seamless transactions and 24/7 customer support for all your trading needs."
    }
];

function LoginPage() {

    const { login } = useContext(AuthContext);
    
    // Update page title
    usePageTitle();
    const [searchParams, setSearchParams] = useSearchParams();
    const showForgotPassword = searchParams.get('forgotpassword') === 'true';
    const navigate = useNavigate();

    // Login form state
    const [loginForm, setLoginForm] = useState({
        emailOrPhone: "",
        password: "",
        showPassword: false,
        error: "",
        termsAccepted: false,
        isLoading: false,
        fieldErrors: {
        emailOrPhone: false,
        password: false,
        terms: false
        }
    });

    // Carousel state
    const [carousel, setCarousel] = useState({
        currentSlide: 0,
        isTransitioning: false
    });

    // Forgot password state
    const [forgotPassword, setForgotPassword] = useState({
        step: 1, // 1: OTP Entry, 2: Set Password, 3: Success
        emailOrPhone: "",
        showOTP: false,
        otp: "",
        isSendingOTP: false,
        isVerifyingOTP: false,
        otpTimer: 0,
        otpError: false,
        newPassword: "",
        confirmPassword: "",
        showNewPassword: false,
        showConfirmPassword: false,
        isResetting: false
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Reset field errors and validate
        const errors = {
            emailOrPhone: !loginForm.emailOrPhone,
            password: !loginForm.password,
            terms: !loginForm.termsAccepted
        };

        // Update state once with all validation results
        if (errors.emailOrPhone || errors.password || errors.terms) {
            const errorMessage = errors.terms 
                ? "Please accept the Privacy Policy and Terms & Conditions to continue"
                : "Please fill in all fields";
            setLoginForm(prev => ({ 
                ...prev, 
                error: errorMessage,
                fieldErrors: errors
            }));
            return;
        }

        // Clear errors and start loading
        setLoginForm(prev => ({ 
            ...prev, 
            error: "",
            fieldErrors: { emailOrPhone: false, password: false, terms: false },
            isLoading: true 
        }));

        try {
            await login(loginForm.emailOrPhone, loginForm.password);
            navigate("/");
        } catch (err) {
            setLoginForm(prev => ({ 
                ...prev, 
                error: err?.response?.data?.detail || "Login Failed"
            }));
        } finally {
            setLoginForm(prev => ({ ...prev, isLoading: false }));
        }
    };

    // Validation functions
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        const phoneRegex = /^\d{10}$/;
        return phoneRegex.test(phone);
    };

    const isValidEmailOrPhone = (input) => {
        return validateEmail(input) || validatePhone(input);
    };

    const togglePasswordVisibility = () => {
        setLoginForm(prev => ({ ...prev, showPassword: !prev.showPassword }));
    };

    const goToSlide = (index) => {
        if (index === carousel.currentSlide || carousel.isTransitioning) return;
        setCarousel(prev => ({ ...prev, isTransitioning: true, currentSlide: index }));
        setTimeout(() => {
            setCarousel(prev => ({ ...prev, isTransitioning: false }));
        }, 500);
    };

    useEffect(() => {
        if (carousel.isTransitioning) return;
        
        const interval = setInterval(() => {
            setCarousel(prev => ({ ...prev, currentSlide: (prev.currentSlide + 1) % carouselSlides.length }));
        }, 5000);

        return () => clearInterval(interval);
    }, [carousel.isTransitioning]);

    // Countdown timer effect for forgot password OTP
    useEffect(() => {
        if (forgotPassword.otpTimer > 0) {
            const timer = setTimeout(() => {
                setForgotPassword(prev => ({ ...prev, otpTimer: prev.otpTimer - 1 }));
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [forgotPassword.otpTimer]);

    // Handle Get Forgot Password OTP
    const handleGetForgotPasswordOTP = async (e) => {
        e.preventDefault();
        setLoginForm(prev => ({ ...prev, error: "" }));

        if (!forgotPassword.emailOrPhone.trim()) {
            setLoginForm(prev => ({ ...prev, error: "Please enter your email or phone number" }));
            return;
        }

        setForgotPassword(prev => ({ ...prev, isSendingOTP: true }));
        try {
            await sendForgotPasswordOTP(forgotPassword.emailOrPhone);
            setForgotPassword(prev => ({ ...prev, showOTP: true, otpTimer: 240 })); // 4 minutes timer
            setLoginForm(prev => ({ ...prev, error: "" }));
        } catch (err) {
            setLoginForm(prev => ({ ...prev, error: err?.response?.data?.detail || err?.response?.data?.message || "Failed to send OTP. Please try again." }));
        } finally {
            setForgotPassword(prev => ({ ...prev, isSendingOTP: false }));
        }
    };

    // Verify Forgot Password OTP (called when Continue button is clicked)
    const handleVerifyForgotPasswordOTP = async () => {
        if (!forgotPassword.otp || forgotPassword.otp.length !== 6) {
            setLoginForm(prev => ({ ...prev, error: "Please enter a valid 6-digit OTP" }));
            return;
        }

        setForgotPassword(prev => ({ ...prev, isVerifyingOTP: true, otpError: false }));
        setLoginForm(prev => ({ ...prev, error: "" }));
        try {
            await verifyForgotPasswordOTP(forgotPassword.emailOrPhone, forgotPassword.otp);
            setForgotPassword(prev => ({ ...prev, otpError: false, step: 2 })); // Move to step 2: Set Password
            setLoginForm(prev => ({ ...prev, error: "" }));
        } catch (err) {
            setForgotPassword(prev => ({ ...prev, otpError: true }));
            setLoginForm(prev => ({ ...prev, error: err?.response?.data?.detail || "Invalid OTP. Please try again." }));
        } finally {
            setForgotPassword(prev => ({ ...prev, isVerifyingOTP: false }));
        }
    };

    // Resend Forgot Password OTP
    const handleResendForgotPasswordOTP = async () => {
        if (forgotPassword.otpTimer > 0) return;
        setForgotPassword(prev => ({ ...prev, otpError: false, otp: "" }));
        await handleGetForgotPasswordOTP({ preventDefault: () => {} });
    };

    // Format timer as MM:SS
    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    // Handle Continue from OTP step
    const handleContinueFromOTP = async (e) => {
        e.preventDefault();
        await handleVerifyForgotPasswordOTP();
    };

    // Handle Reset Password (Confirm button)
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoginForm(prev => ({ ...prev, error: "" }));

        if (!forgotPassword.newPassword || !forgotPassword.confirmPassword) {
            setLoginForm(prev => ({ ...prev, error: "Please fill in all password fields" }));
            return;
        }

        if (forgotPassword.newPassword.length < 8) {
            setLoginForm(prev => ({ ...prev, error: "Password must be at least 8 characters" }));
            return;
        }

        if (forgotPassword.newPassword !== forgotPassword.confirmPassword) {
            setLoginForm(prev => ({ ...prev, error: "Passwords do not match" }));
            return;
        }

        setForgotPassword(prev => ({ ...prev, isResetting: true }));
        try {
            await resetPassword(forgotPassword.emailOrPhone, forgotPassword.otp, forgotPassword.newPassword);
            setLoginForm(prev => ({ ...prev, error: "" }));
            setForgotPassword(prev => ({ ...prev, step: 3 })); // Move to step 3: Success
        } catch (err) {
            setLoginForm(prev => ({ ...prev, error: err?.response?.data?.detail || err?.response?.data?.message || "Failed to reset password. Please try again." }));
        } finally {
            setForgotPassword(prev => ({ ...prev, isResetting: false }));
        }
    };

    // Handle Done button on success screen
    const handleDone = () => {
        setSearchParams({});
        // Reset all forgot password state
        setForgotPassword({
            step: 1,
            emailOrPhone: "",
            showOTP: false,
            otp: "",
            isSendingOTP: false,
            isVerifyingOTP: false,
            otpTimer: 0,
            otpError: false,
            newPassword: "",
            confirmPassword: "",
            showNewPassword: false,
            showConfirmPassword: false,
            isResetting: false
        });
        setLoginForm(prev => ({ ...prev, error: "" }));
    };

    return (
        <div className={styles["container"]}>
            {/* Left Section - Login Form */}
            <div className={styles["right-section"]}>
                <form className={styles["form-container"]} onSubmit={showForgotPassword ? handleResetPassword : handleSubmit}>
                    <h2 className={styles["form-heading"]}>
                        {showForgotPassword 
                            ? (forgotPassword.step === 1 
                                ? "Forgot Password?" 
                                : forgotPassword.step === 2 
                                ? "Set your New Password" 
                                : "Password Reset!")
                            : "Welcome Back"}
                    </h2>
                    <p className={styles["form-subtitle"]}>
                        {showForgotPassword 
                            ? (forgotPassword.step === 1 
                                ? "Enter your registered email to recover your password" 
                                : forgotPassword.step === 2 
                                ? "Your new password should be different from the previously used." 
                                : "")
                            : "Sign in to access your dashboard"}
                    </p>

                    <div className={styles["form-content"]}>
                        {showForgotPassword ? (
                            <>
                                {loginForm.error !== "" && (
                                    <div className={styles["error-message"]}>{loginForm.error}</div>
                                )}

                                {/* Step 1: Enter OTP */}
                                {forgotPassword.step === 1 && (
                                    <>
                                        {/* Email/Phone Input with Get OTP Button */}
                                        <div className={styles["input-group"]}>
                                            <div className={styles["input-wrapper"]}>
                                                <span className={styles["input-icon"]}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="15" viewBox="0 0 19 15" fill="none">
                                                        <path d="M17.5 2.50001C17.5 1.58334 16.75 0.833344 15.8334 0.833344H2.50004C1.58337 0.833344 0.833374 1.58334 0.833374 2.50001M17.5 2.50001V12.5C17.5 13.4167 16.75 14.1667 15.8334 14.1667H2.50004C1.58337 14.1667 0.833374 13.4167 0.833374 12.5V2.50001M17.5 2.50001L9.16671 8.33334L0.833374 2.50001" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </span>
                                                <input
                                                    type="text"
                                                    className={`${styles["input"]} ${styles["email-input"]}`}
                                                    placeholder="Enter your registered email or phone number"
                                                    value={forgotPassword.emailOrPhone}
                                                    onChange={(e) => setForgotPassword(prev => ({ ...prev, emailOrPhone: e.target.value }))}
                                                    disabled={forgotPassword.showOTP}
                                                />
                                                {!forgotPassword.showOTP && isValidEmailOrPhone(forgotPassword.emailOrPhone) && (
                                                    <button
                                                        type="button"
                                                        className={styles["get-otp-button"]}
                                                        onClick={handleGetForgotPasswordOTP}
                                                        disabled={forgotPassword.isSendingOTP}
                                                    >
                                                        {forgotPassword.isSendingOTP ? "Sending..." : "Get OTP"}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* OTP Input Section */}
                                        {forgotPassword.showOTP && (
                                            <>
                                                <OTPInput
                                                    otp={forgotPassword.otp}
                                                    setOtp={(otp) => setForgotPassword(prev => ({ ...prev, otp }))}
                                                    isVerifying={forgotPassword.isVerifyingOTP}
                                                    isVerified={false}
                                                    timer={forgotPassword.otpTimer}
                                                    formatTimer={formatTimer}
                                                    onResend={handleResendForgotPasswordOTP}
                                                    hasError={forgotPassword.otpError}
                                                    onErrorClear={() => setForgotPassword(prev => ({ ...prev, otpError: false }))}
                                                    showResendInHeader={false}
                                                />
                                                
                                                {/* Resend OTP Link */}
                                                <div className={styles["resend-otp-container"]}>
                                                    <span className={styles["resend-otp-text"]}>Didn't receive the OTP? </span>
                                                    <button
                                                        type="button"
                                                        className={styles["resend-otp-link"]}
                                                        onClick={handleResendForgotPasswordOTP}
                                                        disabled={forgotPassword.otpTimer > 0}
                                                    >
                                                        Resend OTP
                                                    </button>
                                                </div>
                                            </>
                                        )}

                                        {/* Continue Button - Always visible in step 1 */}
                                        <button
                                            type="button"
                                            className={`${styles["submit-button"]} ${forgotPassword.isVerifyingOTP ? styles["submit-button-loading"] : ""}`}
                                            onClick={handleContinueFromOTP}
                                            disabled={!forgotPassword.showOTP || forgotPassword.isVerifyingOTP || forgotPassword.otp.length !== 6}
                                        >
                                            {forgotPassword.isVerifyingOTP ? "Verifying..." : "Continue"}
                                        </button>

                                        {/* Separator - Always visible in step 1 */}
                                        <div className={styles["divider"]}>
                                            <span className={styles["divider-text"]}>or</span>
                                        </div>

                                        {/* Back to Sign In Link - Always visible in step 1 */}
                                        <div className={styles["back-to-signin-container"]}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSearchParams({});
                                                    handleDone();
                                                }}
                                                className={styles["back-to-signin-link"]}
                                            >
                                                &lt; Back to Sign In
                                            </button>
                                        </div>
                                    </>
                                )}

                                {/* Step 2: Set New Password */}
                                {forgotPassword.step === 2 && (
                                    <>
                                        {/* Create New Password Input */}
                                        <div className={styles["input-group"]}>
                                            <div className={styles["input-wrapper"]}>
                                                <input
                                                    type={forgotPassword.showNewPassword ? "text" : "password"}
                                                    className={styles["input"]}
                                                    placeholder="Create New Password"
                                                    value={forgotPassword.newPassword}
                                                    onChange={(e) => setForgotPassword(prev => ({ ...prev, newPassword: e.target.value }))}
                                                    disabled={forgotPassword.isResetting}
                                                />
                                                <button
                                                    type="button"
                                                    className={styles["password-toggle"]}
                                                    onClick={() => setForgotPassword(prev => ({ ...prev, showNewPassword: !prev.showNewPassword }))}
                                                    aria-label={forgotPassword.showNewPassword ? "Hide password" : "Show password"}
                                                >
                                                    {forgotPassword.showNewPassword ? (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path d="M2.5 3.75L17.5 16.25M8.15625 8.90625C7.84375 9.21875 7.65625 9.65625 7.65625 10.15625C7.65625 11.1875 8.46875 12 9.5 12C10 12 10.4375 11.8125 10.75 11.5M15.40625 13.34375C14.1875 14.25 12.65625 14.84375 11 14.84375C6.4375 14.84375 2.8125 11.21875 1.25 10C1.9375 8.78125 2.875 7.71875 3.96875 6.84375M8.75 5.03125C9.34375 4.9375 9.96875 4.875 10.625 4.875C15.1875 4.875 18.8125 8.5 20.375 9.71875C19.875 10.5 19.1875 11.21875 18.375 11.8125" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path d="M10 4.375C14.5625 4.375 18.1875 8 19.75 9.21875C19.5 9.75 19.0625 10.2812 18.4375 10.7812M5.5625 5.5625C3.3125 7.0625 1.75 9.125 1.25 10C2.8125 11.2188 6.4375 14.8438 11 14.8438C12.4375 14.8438 13.75 14.5 14.9375 13.9375M2.5 2.5L17.5 17.5M7.34375 7.34375C6.625 8.0625 6.15625 9.03125 6.15625 10.15625C6.15625 12.21875 7.78125 13.84375 9.84375 13.84375C10.96875 13.84375 11.9375 13.375 12.65625 12.65625" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Confirm New Password Input */}
                                        <div className={styles["input-group"]}>
                                            <div className={styles["input-wrapper"]}>
                                                <input
                                                    type={forgotPassword.showConfirmPassword ? "text" : "password"}
                                                    className={styles["input"]}
                                                    placeholder="Confirm New Password"
                                                    value={forgotPassword.confirmPassword}
                                                    onChange={(e) => setForgotPassword(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                                    disabled={forgotPassword.isResetting}
                                                />
                                                <button
                                                    type="button"
                                                    className={styles["password-toggle"]}
                                                    onClick={() => setForgotPassword(prev => ({ ...prev, showConfirmPassword: !prev.showConfirmPassword }))}
                                                    aria-label={forgotPassword.showConfirmPassword ? "Hide password" : "Show password"}
                                                >
                                                    {forgotPassword.showConfirmPassword ? (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path d="M2.5 3.75L17.5 16.25M8.15625 8.90625C7.84375 9.21875 7.65625 9.65625 7.65625 10.15625C7.65625 11.1875 8.46875 12 9.5 12C10 12 10.4375 11.8125 10.75 11.5M15.40625 13.34375C14.1875 14.25 12.65625 14.84375 11 14.84375C6.4375 14.84375 2.8125 11.21875 1.25 10C1.9375 8.78125 2.875 7.71875 3.96875 6.84375M8.75 5.03125C9.34375 4.9375 9.96875 4.875 10.625 4.875C15.1875 4.875 18.8125 8.5 20.375 9.71875C19.875 10.5 19.1875 11.21875 18.375 11.8125" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path d="M10 4.375C14.5625 4.375 18.1875 8 19.75 9.21875C19.5 9.75 19.0625 10.2812 18.4375 10.7812M5.5625 5.5625C3.3125 7.0625 1.75 9.125 1.25 10C2.8125 11.2188 6.4375 14.8438 11 14.8438C12.4375 14.8438 13.75 14.5 14.9375 13.9375M2.5 2.5L17.5 17.5M7.34375 7.34375C6.625 8.0625 6.15625 9.03125 6.15625 10.15625C6.15625 12.21875 7.78125 13.84375 9.84375 13.84375C10.96875 13.84375 11.9375 13.375 12.65625 12.65625" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Password Requirements Text */}
                                        <div className={styles["password-requirements"]}>
                                            Use 8+ characters with a mix of letters and numbers
                                        </div>

                                        {/* Confirm Button */}
                                        <button
                                            type="button"
                                            className={styles["submit-button"]}
                                            onClick={handleResetPassword}
                                            disabled={forgotPassword.isResetting || !forgotPassword.newPassword || !forgotPassword.confirmPassword || forgotPassword.newPassword.length < 8 || forgotPassword.newPassword !== forgotPassword.confirmPassword}
                                        >
                                            {forgotPassword.isResetting ? "Resetting..." : "Confirm"}
                                        </button>

                                        {/* Separator */}
                                        <div className={styles["divider"]}>
                                            <span className={styles["divider-text"]}>or</span>
                                        </div>

                                        {/* Back to Sign In Link */}
                                        <div className={styles["back-to-signin-container"]}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSearchParams({});
                                                    handleDone();
                                                }}
                                                className={styles["back-to-signin-link"]}
                                            >
                                                &lt; Back to Sign In
                                            </button>
                                        </div>
                                    </>
                                )}

                                {/* Step 3: Success Screen */}
                                {forgotPassword.step === 3 && (
                                    <>
                                        <div className={styles["success-container"]}>
                                            {/* Success Checkmark Icon */}
                                            <div className={styles["success-icon"]}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                                                    <circle cx="32" cy="32" r="32" fill="#10B981"/>
                                                    <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </div>

                                            {/* Success Message */}
                                            <p className={styles["success-message"]}>
                                                Your password has been successfully reset, click below to Sign in.
                                            </p>

                                            {/* Done Button */}
                                            <button
                                                type="button"
                                                className={styles["submit-button"]}
                                                onClick={handleDone}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                        {loginForm.error !== "" && (
                            <div className={styles["error-message"]}>{loginForm.error}</div>
                        )}

                        {/* Email/Phone Input */}
                        <div className={styles["input-group"]}>
                            <div className={styles["input-wrapper"]}>
                                <span className={styles["input-icon"]}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="15" viewBox="0 0 19 15" fill="none">
                                        <path d="M17.5 2.50001C17.5 1.58334 16.75 0.833344 15.8334 0.833344H2.50004C1.58337 0.833344 0.833374 1.58334 0.833374 2.50001M17.5 2.50001V12.5C17.5 13.4167 16.75 14.1667 15.8334 14.1667H2.50004C1.58337 14.1667 0.833374 13.4167 0.833374 12.5V2.50001M17.5 2.50001L9.16671 8.33334L0.833374 2.50001" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                <input
                                    type="text"
                                    className={`${styles["input"]} ${styles["email-input"]} ${loginForm.fieldErrors.emailOrPhone ? styles["input-error"] : ""}`}
                                    placeholder="Enter your registered email or phone number"
                                    value={loginForm.emailOrPhone}
                                    onChange={(e) => {
                                        setLoginForm(prev => ({ ...prev, emailOrPhone: e.target.value, fieldErrors: { ...prev.fieldErrors, emailOrPhone: false } }));
                                    }}
                                    disabled={loginForm.isLoading}
                                    aria-label="Email or phone number"
                                    autoComplete="username"
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div className={styles["input-group"]}>
                            <div className={styles["input-wrapper"]}>
                                <input
                                    type={loginForm.showPassword ? "text" : "password"}
                                    className={`${styles["input"]} ${loginForm.fieldErrors.password ? styles["input-error"] : ""}`}
                                    placeholder="Enter Your Password"
                                    value={loginForm.password}
                                    onChange={(e) => {
                                        setLoginForm(prev => ({ ...prev, password: e.target.value, fieldErrors: { ...prev.fieldErrors, password: false } }));
                                    }}
                                    disabled={loginForm.isLoading}
                                    aria-label="Password"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    className={styles["password-toggle"]}
                                    onClick={togglePasswordVisibility}
                                    disabled={loginForm.isLoading}
                                    aria-label={loginForm.showPassword ? "Hide password" : "Show password"}
                                >
                                    {loginForm.showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M2.5 3.75L17.5 16.25M8.15625 8.90625C7.84375 9.21875 7.65625 9.65625 7.65625 10.15625C7.65625 11.1875 8.46875 12 9.5 12C10 12 10.4375 11.8125 10.75 11.5M15.40625 13.34375C14.1875 14.25 12.65625 14.84375 11 14.84375C6.4375 14.84375 2.8125 11.21875 1.25 10C1.9375 8.78125 2.875 7.71875 3.96875 6.84375M8.75 5.03125C9.34375 4.9375 9.96875 4.875 10.625 4.875C15.1875 4.875 18.8125 8.5 20.375 9.71875C19.875 10.5 19.1875 11.21875 18.375 11.8125" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M10 4.375C14.5625 4.375 18.1875 8 19.75 9.21875C19.5 9.75 19.0625 10.2812 18.4375 10.7812M5.5625 5.5625C3.3125 7.0625 1.75 9.125 1.25 10C2.8125 11.2188 6.4375 14.8438 11 14.8438C12.4375 14.8438 13.75 14.5 14.9375 13.9375M2.5 2.5L17.5 17.5M7.34375 7.34375C6.625 8.0625 6.15625 9.03125 6.15625 10.15625C6.15625 12.21875 7.78125 13.84375 9.84375 13.84375C10.96875 13.84375 11.9375 13.375 12.65625 12.65625" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Forget Password Link */}
                        <div className={styles["forget-password-container"]}>
                            <button
                                type="button"
                                onClick={() => setSearchParams({ forgotpassword: 'true' })}
                                className={styles["primary-link"]}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
                            >
                                Forget Password?
                            </button>
                        </div>

                        {/* Terms and Conditions */}
                        <div className={styles["terms-container"]}>
                            <label className={styles["terms-checkbox-label"]}>
                                <input
                                    type="checkbox"
                                    checked={loginForm.termsAccepted}
                                    onChange={(e) => {
                                        setLoginForm(prev => ({ ...prev, termsAccepted: e.target.checked, fieldErrors: { ...prev.fieldErrors, terms: false } }));
                                    }}
                                    className={styles["terms-checkbox"]}
                                    disabled={loginForm.isLoading}
                                    aria-label="Accept terms and conditions"
                                />
                                <span className={styles["terms-text"]}>
                                    By Sign in an account, you agreeing to our{" "}
                                    <Link to="/privacy-policy" className={styles["primary-link"]}>
                                        Privacy Policy
                                    </Link>
                                    , and{" "}
                                    <Link to="/terms-of-service" className={styles["primary-link"]}>
                                        Term & Conditions
                                    </Link>
                                </span>
                            </label>
                        </div>

                        {/* Sign In Button */}
                        <button 
                            type="submit" 
                            className={styles["submit-button"]}
                            disabled={loginForm.isLoading}
                            aria-label="Sign in"
                        >
                            {loginForm.isLoading ? "Signing in..." : "Sign in"}
                        </button>

                        {/* Separator */}
                        <div className={styles["divider"]}>
                            <span className={styles["divider-text"]}>or</span>
                        </div>

                        {/* Sign Up Link */}
                        <div className={styles["signup-link-container"]}>
                            <span className={styles["signup-text"]}>Don't have an account? </span>
                            <Link to="/signup" className={styles["primary-link"]}>Sign up</Link>
                        </div>
                            </>
                        )}
                    </div>
                </form>
            </div>

            {/* Right Section - Carousel */}
            <div className={styles["left-section-wrapper"]}>
                <div className={styles["left-section"]}>
                    {/* Background Pattern SVG */}
                    <div className={styles["bg-pattern"]}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="123" height="136" viewBox="0 0 123 136" fill="none">
                            <rect x="33" width="90" height="84" fill="#D9D9D9" fillOpacity="0.1" />
                            <rect x="1.5" y="53.5" width="87" height="81" stroke="white" strokeOpacity="0.1" strokeWidth="3" />
                        </svg>
                    </div>
                    <div className={styles["logo-container"]}>
                        <svg
                            width="33"
                            height="37"
                            viewBox="0 0 33 37"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={styles["logo"]}
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M32.7149 25.9982V10.7913C32.7149 9.65245 32.1074 8.60009 31.1208 8.0305L17.9515 0.427194C16.9653 -0.142398 15.75 -0.142398 14.7637 0.427194L1.59376 8.0305C0.6075 8.60009 0 9.65245 0 10.7913V25.9982C0 27.1371 0.6075 28.1898 1.59376 28.7591L14.7637 36.3624C15.75 36.9319 16.9653 36.9319 17.9515 36.3624L31.1208 28.7591C32.1074 28.1898 32.7149 27.1371 32.7149 25.9982Z"
                                fill="white"
                            />
                            <path
                                d="M8.23406 18.344C8.23406 18.344 8.26339 20.5587 8.32206 24.988C8.32206 25.296 8.23406 25.5673 8.05806 25.802C7.89673 26.022 7.66206 26.132 7.35406 26.132C7.04606 26.132 6.79673 26.022 6.60606 25.802C6.41539 25.5673 6.32006 25.296 6.32006 24.988L6.43006 18.344L6.32006 11.854C6.32006 11.546 6.41539 11.2747 6.60606 11.04C6.79673 10.8053 7.04606 10.688 7.35406 10.688C7.66206 10.688 7.89673 10.8053 8.05806 11.04C8.23406 11.26 8.32206 11.568 8.32206 11.964C8.32206 12.36 8.30739 13.1373 8.27806 14.296C8.26339 15.44 8.25606 16.1587 8.25606 16.452C9.07739 15.0733 10.2361 13.768 11.7321 12.536C13.2427 11.304 14.4454 10.688 15.3401 10.688C15.6774 10.688 15.9561 10.7907 16.1761 10.996C16.4107 11.2013 16.5281 11.4653 16.5281 11.788C16.5281 12.1107 16.4107 12.3747 16.1761 12.58C15.9561 12.7853 15.7287 12.888 15.4941 12.888C15.2594 12.888 15.0174 12.8293 14.7681 12.712C14.5334 12.58 14.2694 12.514 13.9761 12.514C13.6827 12.514 13.2354 12.734 12.6341 13.174C12.0327 13.614 11.3874 14.2007 10.6981 14.934C10.0381 15.6673 9.50273 16.3567 9.09206 17.002C13.4774 22.6927 18.7794 26.9973 24.9981 29.916C25.9514 30.356 26.4281 30.8547 26.4281 31.412C26.4281 31.7053 26.3254 31.94 26.1201 32.116C25.9147 32.292 25.6287 32.38 25.2621 32.38C24.3527 32.38 22.8274 31.6173 20.6861 30.092C18.5594 28.5667 16.3521 26.7407 14.0641 24.614C13.0227 23.6607 11.9447 22.5827 10.8301 21.38C9.73006 20.1627 8.86473 19.1507 8.23406 18.344Z"
                                fill="#4F46E5"
                            />
                        </svg>
                        <span className={styles["logo-text"]}>KnI Wealth</span>
                    </div>

                    {/* Carousel Container */}
                    <div className={styles["carousel-container"]}>
                        <div
                            className={styles["carousel-track"]}
                            style={{
                                transform: `translateX(-${carousel.currentSlide * 100}%)`,
                                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {carouselSlides.map((slide, index) => (
                                <div key={index} className={styles["carousel-slide"]}>
                                    <div className={styles["pnl-card"]}>
                                        <img
                                            src={slide.image}
                                            alt="Profit preview"
                                            className={styles["pnl-image"]}
                                        />
                                    </div>
                                    <div className={styles["promotional-content"]}>
                                        <h1 className={styles["promo-heading"]}>
                                            {slide.heading}
                                        </h1>
                                        <p className={styles["promo-text"]}>
                                            {slide.text}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Carousel Indicators */}
                    <div className={styles["carousel-indicators"]}>
                        {carouselSlides.map((_, index) => (
                            <button
                                key={index}
                                type="button"
                                className={`${styles["indicator"]} ${index === carousel.currentSlide ? styles["indicator-active"] : ""}`}
                                onClick={() => goToSlide(index)}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginPage;
